import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, NgForm, NG_VALUE_ACCESSOR, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';

import { SimpleToolbar } from 'rev-shared/htmlEditor/Constants';
import { BaseControlValueAccessor } from 'rev-shared/util/BaseControlValueAccessor';
import { ValidationRules } from 'rev-shared/util/ValidationRules';
import { toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';

import { IBanner } from './BannerTypes';

@Component({
	selector: 'banner-add-form',
	templateUrl: './BannerAddForm.Component.html',
	standalone: false,
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BannerAddFormComponent),
		multi: true
	}, {
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => BannerAddFormComponent),
		multi: true
	}]
})
export class BannerAddFormComponent extends BaseControlValueAccessor<IBanner> implements OnInit, AfterViewInit, OnDestroy, Validator {
	@Input() public themedHtmlEditor = false;
	@Input() public isDuplicateName: (linkName: string) => boolean = () => false;

	@Output() public addEntry: EventEmitter<IBanner> = new EventEmitter();
	@Output() public cancel: EventEmitter<void> = new EventEmitter();

	@ViewChild(NgForm, { static: true }) public form!: NgForm;

	public editorConfig = { toolbar: SimpleToolbar };
	public isEditMode = false;

	private validateFn?: () => void;
	private readonly subscription = new Subscription();

	constructor() {
		super();
	}

	public override writeValue(val: IBanner): void {
		super.writeValue(val);
		this.initializeValue();
	}

	public ngOnInit(): void {
		this.initializeValue();
	}

	private initializeValue(): void {
		if (!this.value) {
			this.value = { name: '', message: '', link: '', pushMethod: 'Manual' };
		}
		this.isEditMode = !!this.value?.name?.trim();
	}

	public ngAfterViewInit(): void {
		if (this.form?.statusChanges) {
			this.subscription.add(this.form.statusChanges.subscribe(() => this.validateFn?.()));
		}
	}

	public ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public validate(_: AbstractControl): ValidationErrors | null {
		if (this.form?.invalid) {
			return { invalid: true };
		}

		return null;
	}

	public registerOnValidatorChange(fn: () => void): void {
		this.validateFn = fn;
	}

	public onNameChange(): void {
		toggleError(this.form.controls.eLinkName, 'duplicateName', this.isDuplicateName(this.value.name));
		this.updateModelValue(this.value);
	}

	public validateUrl(): void {
		toggleError(this.form.controls.eLinkUrl, 'invalidCode', !ValidationRules.checkUrl(this.value.link, ['http', 'https']));
		this.updateModelValue(this.value);
	}

	public onMessageChange(): void {
		this.updateModelValue(this.value);
	}

	public onPushMethodChange(): void {
		this.updateModelValue(this.value);
	}

	public onCancelClick(): void {
		this.cancel.emit();
	}

	public onSubmit(): void {
		if (this.form.invalid) {
			return;
		}

		this.addEntry.emit(this.value);
	}
}
